import { useState, useEffect, useRef, useCallback } from "react";
import AppLogo from "./AppLogo";
import { C, FONT } from "./ui/theme";
import { Button, SelectField, ValidationMessage, ProgressSteps, FormKitStyles, Spinner } from "./ui/FormKit";
import { getTrialStatus, resendVerification, submitOnboarding, getSmtpAccounts } from "./api";
import SmtpConnectForm from "./SmtpConnectForm";

const STEP_LABELS = ["Verify Email", "Connect Workspace", "Personalize", "Start Exploring"];
const STEP_KEY = "sdr_onboarding_step";

const GOAL_OPTIONS = [
  { value: "", label: "Select what you're focused on…" },
  { value: "pipeline", label: "Build a bigger pipeline" },
  { value: "outbound", label: "Automate outbound prospecting" },
  { value: "replies", label: "Improve reply & engagement rates" },
  { value: "meetings", label: "Book more meetings" },
  { value: "consolidate", label: "Consolidate tools into one platform" },
  { value: "other", label: "Something else" },
];
const TEAM_SIZE_OPTIONS = [
  { value: "", label: "Select your team size…" },
  { value: "1", label: "Just me" },
  { value: "2-5", label: "2–5 people" },
  { value: "6-20", label: "6–20 people" },
  { value: "21-50", label: "21–50 people" },
  { value: "50+", label: "50+ people" },
];

function Shell({ children, step }) {
  return (
    <div style={{ fontFamily: FONT, minHeight: "100vh", background: C.bgAlt, display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 20px" }}>
      <FormKitStyles />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 36 }}>
        <AppLogo size={26} />
        <span style={{ fontSize: 16, fontWeight: 700, color: C.text, letterSpacing: "-.2px" }}>SDR ReachOut</span>
      </div>
      <div style={{ width: "100%", maxWidth: 560, marginBottom: 40 }}>
        <ProgressSteps steps={STEP_LABELS} currentIndex={step} />
      </div>
      <div style={{ width: "100%", maxWidth: 480, background: "#fff", borderRadius: 20, border: `1px solid ${C.borderSoft}`, boxShadow: "0 2px 24px rgba(0,0,0,0.05)", padding: "40px 36px" }}>
        {children}
      </div>
    </div>
  );
}

function StepHeading({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 28, textAlign: "center" }}>
      <h2 style={{ fontSize: 21, fontWeight: 700, color: C.text, letterSpacing: "-.4px", margin: "0 0 8px" }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 14, color: C.textSub, lineHeight: 1.6, margin: 0 }}>{subtitle}</p>}
    </div>
  );
}

export default function OnboardingFlow({ user, onComplete, addToast }) {
  const [step, setStep] = useState(() => {
    const saved = parseInt(localStorage.getItem(STEP_KEY), 10);
    return Number.isInteger(saved) && saved >= 0 && saved < STEP_LABELS.length ? saved : 0;
  });
  useEffect(() => { localStorage.setItem(STEP_KEY, String(step)); }, [step]);

  const goTo = (n) => setStep(n);
  const next = () => setStep(s => Math.min(s + 1, STEP_LABELS.length - 1));

  const finish = useCallback(() => {
    localStorage.removeItem(STEP_KEY);
    onComplete();
  }, [onComplete]);

  return (
    <Shell step={step}>
      {step === 0 && <VerifyEmailStep user={user} onContinue={next} addToast={addToast} />}
      {step === 1 && <ConnectWorkspaceStep onContinue={next} onBack={() => goTo(0)} addToast={addToast} />}
      {step === 2 && <PersonalizeStep onContinue={next} onBack={() => goTo(1)} />}
      {step === 3 && <StartExploringStep onFinish={finish} />}
    </Shell>
  );
}

// ─── Step 1: Verify Email ────────────────────────────────────────────────
// Verification runs in the background rather than gating the rest of the
// flow — clicking the emailed link usually happens in a different tab, so
// this polls trial-status instead of waiting on a same-tab redirect.
function VerifyEmailStep({ user, onContinue, addToast }) {
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendError, setResendError] = useState("");
  const pollRef = useRef(null);

  const check = useCallback(async () => {
    try {
      const status = await getTrialStatus();
      if (status.emailVerified) { setVerified(true); if (pollRef.current) clearInterval(pollRef.current); }
    } catch { /* transient — keep polling */ }
    setChecking(false);
  }, []);

  useEffect(() => {
    check();
    pollRef.current = setInterval(check, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [check]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResend = async () => {
    setResendError(""); setResending(true);
    try {
      const res = await resendVerification();
      if (res.alreadyVerified) { setVerified(true); }
      else { addToast && addToast("Verification email sent — check your inbox.", "success"); setResendCooldown(60); }
    } catch (e) {
      setResendError(e?.message || "Couldn't resend the email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <StepHeading
        title={verified ? "Email verified" : "Verify your email"}
        subtitle={verified
          ? "You're all set — this email address is confirmed."
          : <>We sent a link to <strong style={{ color: C.text }}>{user?.email}</strong>. Click it to confirm this is really you.</>}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%",
          background: verified ? C.greenSoft : C.accentSoft,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26,
        }}>
          {verified ? "✓" : checking ? <Spinner size={22} /> : "✉"}
        </div>

        {!verified && (
          <>
            <Button variant="secondary" size="sm" onClick={handleResend} loading={resending} disabled={resendCooldown > 0}>
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend verification email"}
            </Button>
            <ValidationMessage>{resendError}</ValidationMessage>
          </>
        )}

        <div style={{ width: "100%", marginTop: 8 }}>
          <Button onClick={onContinue} style={{ width: "100%" }} disabled={!verified && checking}>
            {verified ? "Continue →" : "I'll verify later →"}
          </Button>
        </div>
      </div>
    </>
  );
}

// ─── Step 2: Connect Your Workspace ──────────────────────────────────────
// Full-page OAuth redirect (see server.js /api/oauth/*/start) reloads the
// whole SPA — the step index persisted in localStorage (see STEP_KEY above)
// is what brings the user back to this exact step instead of the start.
function ConnectWorkspaceStep({ onContinue, onBack, addToast }) {
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);
  const [connecting, setConnecting] = useState(null); // "google" | "microsoft" | null
  const [showSmtp, setShowSmtp] = useState(false);

  const recheck = useCallback(() => {
    setChecking(true);
    return getSmtpAccounts()
      .then(list => setConnected(Array.isArray(list) && list.length > 0))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => { recheck(); }, [recheck]);

  const startConnect = (provider) => {
    setConnecting(provider);
    const token = localStorage.getItem("sdr_token");
    window.location.href = `/api/oauth/${provider}/start?token=${encodeURIComponent(token)}`;
  };

  return (
    <>
      <StepHeading
        title={connected ? "Workspace connected" : "Connect your workspace"}
        subtitle={connected
          ? "Your mailbox is connected and ready to send from."
          : "Connect Google, Microsoft, or any other provider (GoDaddy, Zoho, a custom domain mailbox, etc.) so SDR ReachOut can send and track email on your behalf. Whichever mailbox you connect first here automatically becomes your primary sending account — no extra step needed."}
      />
      {checking ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}><Spinner size={24} /></div>
      ) : connected ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.greenSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>✓</div>
          <Button onClick={onContinue} style={{ width: "100%" }}>Continue →</Button>
        </div>
      ) : showSmtp ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SmtpConnectForm
            addToast={addToast}
            onCancel={() => setShowSmtp(false)}
            onConnected={() => { setShowSmtp(false); recheck(); }}
          />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Button variant="secondary" onClick={() => startConnect("google")} loading={connecting === "google"} style={{ width: "100%" }}>
            Connect Google
          </Button>
          <Button variant="secondary" onClick={() => startConnect("microsoft")} loading={connecting === "microsoft"} style={{ width: "100%" }}>
            Connect Microsoft
          </Button>
          <Button variant="secondary" onClick={() => setShowSmtp(true)} style={{ width: "100%" }}>
            Connect via SMTP (any other provider)
          </Button>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <Button variant="ghost" onClick={onBack} style={{ flex: 1 }}>Back</Button>
            <Button variant="ghost" onClick={onContinue} style={{ flex: 1 }}>Skip for now</Button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Step 3: Personalize ─────────────────────────────────────────────────
function PersonalizeStep({ onContinue, onBack }) {
  const [goal, setGoal] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleContinue = async () => {
    setSaving(true); setError("");
    try {
      await submitOnboarding({ goal: goal || null, teamSize: teamSize || null });
      onContinue();
    } catch (e) {
      setError(e?.message || "Couldn't save that — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StepHeading title="A couple of quick questions" subtitle="This helps us tailor your dashboard — takes 10 seconds." />
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <SelectField id="goal" label="What do you want to accomplish?" value={goal} onChange={e => setGoal(e.target.value)} options={GOAL_OPTIONS} />
        <SelectField id="teamSize" label="How big is your sales team?" value={teamSize} onChange={e => setTeamSize(e.target.value)} options={TEAM_SIZE_OPTIONS} />
        <ValidationMessage>{error}</ValidationMessage>
        <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
          <Button variant="ghost" onClick={onBack} style={{ flex: 1 }}>Back</Button>
          <Button onClick={handleContinue} loading={saving} style={{ flex: 1 }}>Continue →</Button>
        </div>
      </div>
    </>
  );
}

// ─── Step 4: Start Exploring ──────────────────────────────────────────────
function StartExploringStep({ onFinish }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.accentSoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700, color: C.accent, margin: "0 auto 20px" }}>✓</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.text, letterSpacing: "-.4px", margin: "0 0 10px" }}>You're all set</h2>
      <p style={{ fontSize: 14, color: C.textSub, lineHeight: 1.6, margin: "0 0 28px" }}>
        Your 7-day free trial is active. Explore Account Intelligence, launch your first sequence, or just look around.
      </p>
      <Button onClick={onFinish} size="lg" style={{ width: "100%" }}>Start Exploring →</Button>
    </div>
  );
}
